/** Bootstrap Icons glyphs for entity types; public/osint-logo images for platform brands. */

import { createGraphPlatformIconDataUrl, resolveGraphPlatformIconSpec } from './platform-icon.util';

const GLYPH = {
  telephone: '\uf5b4',
  envelope: '\uf32c',
  person: '\uf4e1',
  geo: '\uf3e8',
  image: '\uf42a',
  shield: '\uf53f',
  globe: '\uf3ee',
  at: '\uf152',
  building: '\uf1dd',
  share: '\uf52e',
};

export interface LayeredGraphIcon {
  iconColor: string;
  graphIcon: string;
  iconFontSize: number;
  /** When set, Cytoscape should render this SVG instead of a font glyph. */
  iconSvg?: string;
}

export interface LayeredGraphNode {
  type?: string;
  label?: string;
  metadata?: {
    platform?: string;
    sourceName?: string;
    sourcePlatform?: string;
    isSourceTypeHub?: boolean;
    hubSourceType?: string;
  };
}

function iconSpec(
  glyph: string,
  color: string,
  iconFontSize = 18,
  iconSvg?: string,
): LayeredGraphIcon {
  return { iconColor: color, graphIcon: glyph, iconFontSize, iconSvg };
}

const TYPE_ICONS: Record<string, LayeredGraphIcon> = {
  phone: iconSpec(GLYPH.telephone, '#22c55e'),
  email: iconSpec(GLYPH.envelope, '#3b82f6'),
  email_hint: iconSpec(GLYPH.envelope, '#94a3b8'),
  phone_hint: iconSpec(GLYPH.telephone, '#94a3b8'),
  aggregate_phones: iconSpec(GLYPH.telephone, '#ebd694'),
  aggregate_emails: iconSpec(GLYPH.envelope, '#a4daae'),
  aggregate_names: iconSpec(GLYPH.person, '#4f7dc9'),
  aggregate_photos: iconSpec(GLYPH.image, '#5b8fb9'),
  person: iconSpec(GLYPH.person, '#f59e0b'),
  social_profile: iconSpec(GLYPH.share, '#a855f7'),
  osint_source: iconSpec(GLYPH.share, '#14b8a6'),
  image: iconSpec(GLYPH.image, '#64748b'),
  face_image: iconSpec(GLYPH.image, '#5b8fb9'),
  image_query: iconSpec(GLYPH.image, '#38bdf8'),
  face_result: iconSpec(GLYPH.image, '#72c7ff'),
  leak_source: iconSpec(GLYPH.shield, '#ef5350'),
  location: iconSpec(GLYPH.geo, '#e2761b'),
  address: iconSpec(GLYPH.geo, '#e2761b'),
  website: iconSpec(GLYPH.globe, '#8d6e63'),
  username: iconSpec(GLYPH.at, '#7cb342', 20),
  company: iconSpec(GLYPH.building, '#78909c'),
  starter: iconSpec(GLYPH.telephone, '#22c55e', 24),
  query_root: iconSpec(GLYPH.telephone, '#22c55e', 26),
  ai_analysis: iconSpec(GLYPH.person, '#8b5cf6', 20),
  source_type_hub: iconSpec(GLYPH.share, '#14b8a6', 22),
};

const TYPE_FIRST_TYPES = new Set([
  'phone',
  'email',
  'email_hint',
  'phone_hint',
  'aggregate_phones',
  'aggregate_emails',
  'aggregate_names',
  'aggregate_photos',
  'person',
  'address',
  'image',
  'face_image',
  'image_query',
  'face_result',
  'leak_source',
  'location',
  'company',
  'query_root',
]);

const DEFAULT_ICON = iconSpec(GLYPH.globe, '#64748b');

function resolveBrandIcon(node: LayeredGraphNode): LayeredGraphIcon | null {
  const platform = [
    node?.metadata?.platform,
    node?.metadata?.sourceName,
    node?.metadata?.sourcePlatform,
    node?.label,
  ]
    .map((value) => String(value || '').replace(/^osint\s*source:\s*/i, '').trim())
    .find(Boolean);
  if (!platform) return null;

  const spec = resolveGraphPlatformIconSpec(platform);
  return iconSpec('', spec.accent, 18, createGraphPlatformIconDataUrl(platform));
}

function resolveTypeIcon(type: string, isRoot = false): LayeredGraphIcon {
  if (type === 'query_root') {
    return iconSpec(GLYPH.telephone, '#22c55e', isRoot ? 26 : 22);
  }
  if (isRoot && type === 'email') {
    return iconSpec(GLYPH.envelope, '#3b82f6', 26);
  }
  if (isRoot && type === 'phone') {
    return iconSpec(GLYPH.telephone, '#22c55e', 26);
  }
  if (isRoot) {
    return TYPE_ICONS[type] || iconSpec(GLYPH.telephone, '#22c55e', 26);
  }
  return TYPE_ICONS[type] || DEFAULT_ICON;
}

export function resolveLayeredGraphIcon(
  node: LayeredGraphNode,
  isRoot: boolean,
): LayeredGraphIcon;
export function resolveLayeredGraphIcon(
  node: LayeredGraphNode,
  brand: string | null,
  isRoot: boolean,
): LayeredGraphIcon;
export function resolveLayeredGraphIcon(
  node: LayeredGraphNode,
  second: boolean | string | null,
  third?: boolean,
): LayeredGraphIcon {
  const isRoot = typeof third === 'boolean' ? third : typeof second === 'boolean' ? second : false;
  const type = String(node?.type || 'unknown').toLowerCase();

  if (node?.metadata?.isSourceTypeHub) {
    const hubType = String(node.metadata.hubSourceType || type).toLowerCase();
    const hubIcon = TYPE_ICONS[hubType] || TYPE_ICONS['source_type_hub'];
    return isRoot
      ? { ...hubIcon, iconFontSize: Math.max(hubIcon.iconFontSize, 24) }
      : hubIcon;
  }

  if (TYPE_FIRST_TYPES.has(type)) {
    return resolveTypeIcon(type, isRoot);
  }

  if (type === 'social_profile' || type === 'osint_source' || type === 'website' || type === 'username') {
    return resolveBrandIcon(node) || resolveTypeIcon(type, isRoot);
  }

  if (isRoot) {
    return resolveTypeIcon(type, true);
  }

  return resolveBrandIcon(node) || resolveTypeIcon(type, isRoot);
}
